// Copyright (c) 2020-2023 Visual Intelligence LP. All rights reserved.
//
import { observable, action, makeObservable } from 'mobx';
import { DeliverableUnitType, DeliverableStatus, ReadyStatusType } from '@shared/constants/deliverable';

export interface DeliverableTypeDTO {
  id: number;
  name: string;
  index: number;
  type: number;
  description: string;
}

export interface DeliverableDownloadAccessDTO {
  link: string;
  userName: string;
  password: string;
  notes: string;
}
// Struct to store deliverable due date
export interface DeliverableDueDate {
  deliverableId: number;
  dueDate: Date;
}
// Struct to store info for a Delis received from portal-api.
export interface DeliverableDTO {
  id: number;
  dimensionId: number;
  deliverableType: DeliverableTypeDTO['id'];
  deliverableTypeName?: string;
  deliverableTypeDescription?: string;
  projectionId: number;
  datumId: number;
  unitType: DeliverableUnitType;
  isInternal: boolean;
  status: DeliverableStatus;
  availableStatuses?: Array<DeliverableStatus>;
  projectionName?: string;
  datumName?: string;
  createdAt?: number;
  index?: number;
  timeInMinutes: number;
  lastComment: string;
  lastCommentDateTime: Date;
  exceptionCode: string;
  lastModificationDate: string;
  vendorId: number;
  vendorName: string;
  possibleVendors: {[key: number]: string}; 
  readyStatus?: ReadyStatusType;
  variance: number;
  // Date vars.
  deadline: string;                               // col: 'Plan end' and 'Due' or 'Due date'
  startDate: string;                              // col: Actual start
  planStartDate: string;                          // col: Plan start
  forecastStartDate: string;                      // col: Forecast start
  forecastCompletionDate: string;                 // col: Forecast end
  actualCompletionDate: string;                   // col: Actual end
}

// Note: Remove @observable (?)
export default class Deliverable {
  id: number;
  @observable availableStatuses: Array<DeliverableStatus>;
  @observable status: DeliverableStatus;
  @observable dimensionId: number;
  @observable deliverableType: number;
  @observable deliverableTypeName: string;
  @observable deliverableTypeDescription: string;
  @observable projectionName: string;
  @observable projectionId: number;
  @observable datumName: string;
  @observable datumId: number;
  @observable unitType: DeliverableUnitType;
  @observable isInternal: boolean;
  @observable createdAt: number;
  @observable index: number;
  @observable timeInMinutes: number;
  @observable lastComment: string;
  @observable lastCommentDateTime: Date;
  @observable exceptionCode: string;
  @observable lastModificationDate: string;
  @observable vendorId: number;
  @observable vendorName: string;
  @observable possibleVendors: {[key: number]: string}; 
  @observable readyStatus: ReadyStatusType;
  @observable variance: number;
  @observable deadline: string;
  @observable startDate: string;
  @observable planStartDate: string;
  @observable forecastStartDate: string;
  @observable forecastCompletionDate: string;
  @observable actualCompletionDate: string;

  constructor(dto: DeliverableDTO) {
    this.update(dto);
    makeObservable(this);
  }

  get asJson(): DeliverableDTO {
    return {
      id: this.id,
      availableStatuses: this.availableStatuses,
      dimensionId: this.dimensionId,
      deliverableType: this.deliverableType,
      deliverableTypeName: this.deliverableTypeName,
      projectionName: this.projectionName,
      projectionId: this.projectionId,
      datumName: this.datumName,
      datumId: this.datumId,
      unitType: this.unitType,
      isInternal: this.isInternal,
      status: this.status,
      createdAt: this.createdAt,
      index: this.index,
      timeInMinutes: this.timeInMinutes,
      lastComment: this.lastComment,
      lastCommentDateTime: this.lastCommentDateTime,
      exceptionCode: this.exceptionCode,
      lastModificationDate: this.lastModificationDate,
      vendorId: this.vendorId,
      vendorName: this.vendorName,
      possibleVendors: this.possibleVendors,
      readyStatus: this.readyStatus,
      variance: this.variance,
      deadline: this.deadline,
      startDate: this.startDate,
      planStartDate: this.planStartDate,
      forecastStartDate: this.forecastStartDate,
      forecastCompletionDate: this.forecastCompletionDate,
      actualCompletionDate: this.actualCompletionDate,
    };
  }

  @action update(newData: Partial<DeliverableDTO>): DeliverableDTO {
    const allData = { ...this.asJson, ...newData };

    Object.assign(this, allData);

    return this.asJson;
  }
}